import { submittedFormId } from '@theme/brand-customer-form-scope';

/**
 * <newsletter-popup> — welcome-offer popup (sections/newsletter-popup.liquid).
 *
 * Native <dialog> + showModal(): the browser already makes everything outside the dialog inert
 * while it's open, which is what gives Tab its trap for free — no manual focus-cycling needed.
 * Escape is native too (the browser closes the dialog and fires `close`); a click that lands on
 * the ::backdrop still targets the <dialog> element itself, so a bounding-box check on that one
 * click listener covers backdrop-dismiss without a second overlay element.
 *
 * The form is sent without reloading the page by <brand-newsletter-form>
 * (assets/brand-newsletter-form.js), which reports the outcome with `newsletter:subscribed` (the
 * popup then swaps its form panel for the success one) or `newsletter:failed`. Shopify's captcha
 * runs first and renders its challenge in the page, where a modal dialog would leave it inert and
 * hidden: on submit the dialog is therefore reopened non-modal, which keeps it on screen without
 * blocking what Shopify shows, and it becomes modal again as soon as the outcome is known. A
 * challenge the visitor abandons reports no outcome: the dialog then stays non-modal, and Escape
 * is handled here since the browser only closes modal dialogs. The server-rendered state
 * (data-server-state, data-has-error) only matters when the page did reload: no JavaScript in the
 * form's way, or a full-page verification imposed by Shopify.
 *
 * Two storage flags share the key `locks-passion-newsletter-popup`:
 * - localStorage: the visitor subscribed, the popup never comes back. The Lovable mockup and
 *   scripts/visual/compare.mjs write the same flag to keep the popup out of visual comparisons.
 * - sessionStorage: the popup was shown during this session, it does not come back until the next.
 */
const STORAGE_KEY = 'locks-passion-newsletter-popup';
const FLAG = '1';

/** @param {'localStorage' | 'sessionStorage'} area */
function isFlagged(area) {
  try {
    return window[area].getItem(STORAGE_KEY) === FLAG;
  } catch {
    return false;
  }
}

/** @param {'localStorage' | 'sessionStorage'} area */
function flag(area) {
  // The editor opens this dialog purely to preview it (shopify:section:select below). Writing a
  // flag from there would hide the popup from the merchant's own storefront view afterwards:
  // storage is scoped to the browser, not to the editor.
  if (window.Shopify?.designMode) return;
  try {
    window[area].setItem(STORAGE_KEY, FLAG);
  } catch {
    // Storage unavailable (private browsing, quota): degrade to "always eligible", never crash.
  }
}

class NewsletterPopup extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  /** @type {Element | null} */
  #previouslyFocused = null;

  connectedCallback() {
    this.#init();
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
    document.removeEventListener('close', this.#onForeignModalClose, { capture: true });
    document.removeEventListener('newsletter:subscribed', this.#onSubscribed);
    this.removeEventListener('newsletter:failed', this.#restoreModal);
  }

  #onSectionLoad = (event) => {
    if (event.target?.contains?.(this) || event.target === this) this.#init();
  };

  #onSectionSelect = (event) => {
    if (event.detail?.sectionId === this.sectionId) this.#open();
  };

  #onSectionDeselect = (event) => {
    if (event.detail?.sectionId === this.sectionId) this.dialog?.close();
  };

  #init() {
    const dialog = this.querySelector('dialog');
    if (!dialog || dialog.dataset.newsletterPopupBound) return;
    dialog.dataset.newsletterPopupBound = 'true';

    this.dialog = dialog;
    this.sectionId = this.dataset.sectionId;

    this.querySelectorAll('[data-newsletter-popup-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });

    // A click on the native ::backdrop still targets the <dialog> element itself; anything
    // inside the visible card lands on a descendant instead, so a bounding-box check on the
    // dialog's own click listener is enough to tell the two apart.
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    });

    // `close` is dispatched asynchronously: when it comes from #releaseModal the dialog is already
    // open again, and the focus must stay inside it.
    dialog.addEventListener('close', () => {
      if (!dialog.open) this.#restoreFocus();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !dialog.matches(':modal')) dialog.close();
    });

    this.querySelector('form')?.addEventListener('submit', this.#releaseModal);
    document.addEventListener('newsletter:subscribed', this.#onSubscribed);
    this.addEventListener('newsletter:failed', this.#restoreModal);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
      return;
    }

    // Shopify bakes the result of a `customer` submission into every `customer` form of the page,
    // this popup included, so the state alone never says the popup was the form submitted:
    // assets/brand-customer-form-scope.js records which one was.
    const ownsSubmission = submittedFormId() === this.dataset.formId;

    if (this.dataset.serverState === 'success') {
      // A subscription made in the footer or in the newsletter section counts as well: the offer
      // has nothing left to ask. The confirmation itself is only shown here once, for the popup's
      // own submission; reloading the confirmed URL does not bring it back.
      const alreadyConfirmed = isFlagged('localStorage');
      flag('localStorage');
      if (ownsSubmission && !alreadyConfirmed) this.#open();
    } else if (ownsSubmission && this.dataset.hasError === 'true') {
      this.#open();
    } else {
      this.#maybeSchedule();
    }
  }

  #maybeSchedule() {
    if (isFlagged('localStorage') || isFlagged('sessionStorage')) return;
    const delaySeconds = Number(this.dataset.delay) || 5;
    this.#timer = window.setTimeout(() => this.#openWhenClear(), Math.max(1, delaySeconds) * 1000);
  }

  // Never opens on top of another dialog or Horizon drawer already on screen (cart, filters,
  // quick add...) — two stacked modals trap focus in the wrong one and confuse Escape/backdrop
  // clicks. Defers to the moment the blocking one closes rather than polling for it: the native
  // 'close' event on a <dialog> doesn't bubble, but a capture-phase listener on `document` still
  // reaches it (document is an ancestor of every dialog regardless of bubbling), and a
  // <theme-drawer>'s own panel is a <dialog> too, so the same listener covers both without
  // depending on theme-drawer's separate (bubbling) open/close events.
  #openWhenClear() {
    if (this.#hasForeignModalOpen()) {
      document.addEventListener('close', this.#onForeignModalClose, { capture: true });
      return;
    }
    this.#open();
  }

  #onForeignModalClose = (event) => {
    if (event.target === this.dialog) return;
    if (this.#hasForeignModalOpen()) return;
    document.removeEventListener('close', this.#onForeignModalClose, { capture: true });
    this.#open();
  };

  #hasForeignModalOpen() {
    const modal = document.querySelector('dialog:modal');
    if (modal && modal !== this.dialog) return true;
    return document.querySelector('theme-drawer[open]') !== null;
  }

  #open() {
    if (!this.dialog || this.dialog.open) return;
    this.#previouslyFocused = document.activeElement;
    this.dialog.showModal();
    flag('sessionStorage');
    // Without an autofocus candidate, showModal() falls back to focusing the first focusable
    // descendant — the close (X) button — showing a focus ring nobody triggered by keyboard.
    // Move focus to the visible panel's heading instead, which also announces the dialog's
    // purpose to screen readers. Skip it when a real autofocus target exists (the erroring email
    // input, server-rendered on a failed submission): that focus is more useful and wins.
    if (!this.dialog.querySelector('[autofocus]')) this.#focusInitial();
  }

  #focusInitial() {
    const panel = this.dialog.querySelector(
      this.dialog.dataset.state === 'success' ? '.newsletter-popup__panel--success' : '.newsletter-popup__panel--form',
    );
    // Excludes the visually-hidden fallback title (rendered when the heading/success_heading
    // setting is cleared in the editor): base.css's sr-only pattern reveals it, unstyled, the
    // instant it receives focus — the dialog's accessible name still comes from it via
    // aria-labelledby either way, so falling back to the panel itself here avoids that visible
    // jump without losing anything a screen reader announces.
    const target = panel?.querySelector('h2:not(.visually-hidden)') || panel;
    if (target instanceof HTMLElement) {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  }

  #restoreFocus() {
    const target = this.#previouslyFocused;
    if (target instanceof HTMLElement && document.contains(target)) target.focus();
    this.#previouslyFocused = null;
  }

  /** @param {Event} event */
  #onSubscribed = (event) => {
    flag('localStorage');
    const dialog = this.dialog;
    if (!dialog) return;

    // A subscription made in the footer or in the newsletter section counts as well: the offer has
    // nothing left to ask, so a popup still waiting for its delay never opens.
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      window.clearTimeout(this.#timer);
      document.removeEventListener('close', this.#onForeignModalClose, { capture: true });
      return;
    }

    dialog.dataset.state = 'success';
    const title = dialog.querySelector('.newsletter-popup__panel--success h2');
    if (title) dialog.setAttribute('aria-labelledby', title.id);
    this.#restoreModal();
    this.#focusInitial();
  };

  // Released for the captcha by #releaseModal: the submission is over, the dialog gets its
  // backdrop, its focus trap and its native Escape back.
  #restoreModal = () => {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.matches(':modal')) return;
    dialog.close();
    dialog.showModal();
  };

  #releaseModal = (event) => {
    const dialog = this.dialog;
    if (!dialog?.matches(':modal')) return;
    dialog.close();
    dialog.show();
    if (event.submitter instanceof HTMLElement) event.submitter.focus({ preventScroll: true });
  };
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
